/**
 * Permanent navigation overlay docked to the top-right of the canvas pane.
 *
 * Rendered INSIDE the canvas pane (a `position: relative` flex column) so
 * its `position: absolute; right: 14` tracks the canvas's right edge —
 * which itself moves left/right as the drawer opens, widens, or closes.
 * That way the search + key slide with the drawer instead of being
 * occluded by it.
 *
 * Contents:
 *   - Settings + Contribute icon buttons. (Search moved into the Search
 *     tab's faceted filter — the canvas toolbar no longer has its own
 *     search box.)
 *   - Collapsible cluster reference key (the KEY dropdown) that lists
 *     every active cluster with its color, name, and member count. Click
 *     a row → setFocusedClusterId → CameraDirector zooms to the cluster's
 *     live centroid.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '../state/store';
import { clusterIdsFor } from '../data/cluster';
import { CLUSTER_COLORS, clusterColorForId } from '../zones/palette';
import { advantageClusterLabel } from './tag-labels';
import type { ClusterBy, Move, Position } from '../types/domain';
import { feedbackConfigured } from './feedback-config';
import { SettingsMenu } from './SettingsMenu';
import { FONT, GLASS, LINE, LINE2, INK, INK2, BLUE, RADIUS, activePill } from './tokens';

const HASHED_CLUSTER_TYPES = new Set<ClusterBy>(['family', 'concept']);

const CLUSTER_LABEL_PRETTY: Record<string, string> = {
  top: 'Top', bottom: 'Bottom', neutral: 'Neutral', terminal: 'Terminal', flux: 'Flux',
  sweep: 'Sweeps', submission: 'Submissions', pass: 'Passes', escape: 'Escapes',
  takedown: 'Takedowns', transition: 'Transitions', 'guard-retention': 'Guard Retention',
  choke: 'Chokes', 'joint-lock': 'Joint Locks', 'leg-lock': 'Leg Locks',
  'neck-crank': 'Neck Cranks', other: 'Other',
  defensive: 'Defensive', offensive: 'Offensive', transitional: 'Transitional',
  // posture
  standing: 'Standing', seated: 'Seated', supine: 'Supine',
  prone: 'Prone', turtle: 'Turtle', inverted: 'Inverted',
};
function prettyCluster(id: string, by?: ClusterBy): string {
  // Advantage first: it is the one axis whose ids are foreign words, and
  // `neutral` needs the axis to disambiguate it from position type.
  const adv = by && advantageClusterLabel(id, by);
  if (adv) return adv;
  return CLUSTER_LABEL_PRETTY[id] ?? id.split(/[-_]/)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}
function colorForCluster(id: string, by: ClusterBy): string {
  if (CLUSTER_COLORS[id]) return CLUSTER_COLORS[id];
  if (HASHED_CLUSTER_TYPES.has(by)) {
    // Pass the axis through so the allocator ranks against the right
    // universe (family ids vs concept ids).
    return clusterColorForId(id, by as 'family' | 'concept');
  }
  return '#cad3df';
}

export function NavOverlay({
  positions,
  moves,
}: {
  positions: Position[];
  moves: Move[];
}) {
  const clusterBy = useAppStore((s) => s.clusterBy);
  const focusedClusterId = useAppStore((s) => s.focusedClusterId);
  const setFocusedClusterId = useAppStore((s) => s.setFocusedClusterId);
  const openFeedback = useAppStore((s) => s.openFeedback);

  // Measure the toolbar (search + settings + contribute) so the
  // cluster KEY dropdown below can match its exact width — the user
  // wants the key box to span the same length as the control cluster
  // rather than sizing to its own content.
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarWidth, setToolbarWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const sync = () => setToolbarWidth(el.getBoundingClientRect().width);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Per-row hide state driven by the colour-swatch click. Resets
  // automatically on cluster-axis or mode change (see store).
  const hiddenClusterIds = useAppStore((s) => s.hiddenClusterIds);
  const toggleHiddenClusterId = useAppStore((s) => s.toggleHiddenClusterId);
  const setHiddenClusterIds = useAppStore((s) => s.setHiddenClusterIds);
  const clearHiddenClusterIds = useAppStore((s) => s.clearHiddenClusterIds);

  const positionsById = useMemo(
    () => new Map(positions.map((p) => [p.id, p])),
    [positions],
  );
  const movesById = useMemo(() => new Map(moves.map((m) => [m.id, m])), [moves]);

  // Cluster reference rows. Uses clusterIdsFor (multi-membership) so a
  // node with several concept tags counts toward every concept cluster
  // it belongs to — exposes the full picture of which entities live
  // under each cluster instead of only the first tag in their list.
  const clusterEntries = useMemo(() => {
    if (clusterBy === 'none') return [];
    const counts = new Map<string, number>();
    const candidates: Array<{ id: string; kind: 'position' | 'move' }> = [
      ...positions.map((p) => ({ id: p.id, kind: 'position' as const })),
      ...moves.map((m) => ({ id: m.id, kind: 'move' as const })),
    ];
    for (const c of candidates) {
      const fakeNode = { id: c.id, kind: c.kind } as Parameters<typeof clusterIdsFor>[0];
      const ids = clusterIdsFor(fakeNode, clusterBy, positionsById, movesById);
      for (const cid of ids) counts.set(cid, (counts.get(cid) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([id, count]) => ({
        id,
        count,
        color: colorForCluster(id, clusterBy),
        label: prettyCluster(id, clusterBy),
      }))
      .sort((a, b) => b.count - a.count);
  }, [clusterBy, positions, moves, positionsById, movesById]);


  // Cluster key collapse state. Defaults to closed — the canvas should
  // open with a quiet HUD, and users can pop the key open when they
  // need it. Lifted to the store (keyPanelOpen) so the onboarding coach
  // can open it on the colour step and collapse it afterwards; the user's
  // manual toggle still drives the same flag.
  const effectiveKeyOpen = useAppStore((s) => s.keyPanelOpen);
  const setKeyOpen = useAppStore((s) => s.setKeyPanelOpen);
  // Onboarding tour flashes the element it's pointing at.
  const coachHighlight = useAppStore((s) => s.coachHighlight);
  const flashContribute =
    coachHighlight === 'contribute'
      ? 'bjjmap-ctrl-flash 2.2s ease-in-out infinite'
      : undefined;

  return (
    <div
      style={{
        // Docked inside the canvas pane — slides with the drawer.
        position: 'absolute',
        top: 14,
        right: 14,
        zIndex: 30,
        color: INK,
        fontSize: 12,
        userSelect: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        // Auto-width container so it shrinks to icon-only when search
        // is collapsed and grows just enough for the compact input
        // when expanded.
        width: 'auto',
        alignItems: 'flex-end',
        maxHeight: 'calc(100% - 28px)',
      }}
    >
      {/* Top-right toolbar: search + settings + contribute icons.
          Measured (toolbarRef) so the cluster KEY dropdown below can
          span the exact same width as this button cluster. */}
      <div
        ref={toolbarRef}
        style={{ display: 'flex', gap: 6, alignItems: 'center' }}
      >
        {/* Settings cog — leftmost of the toolbar now that search is
            gone. Tapping toggles the options panel. */}
        <SettingsMenu />

        {/* Contribute / feedback — rightmost, so it reads as the
            standout call-to-action (order swapped with settings per
            request). Only shown once the form is configured. */}
        {feedbackConfigured && (
          <button
            type="button"
            data-tour="contribute"
            aria-label="Contribute / send feedback"
            title="Contribute to the map"
            onClick={() => openFeedback(null)}
            style={{
              width: 36,
              height: 36,
              padding: 0,
              borderRadius: RADIUS.sm,
              background: GLASS.panel,
              border: `1px solid ${LINE2}`,
              animation: flashContribute,
              backdropFilter: 'blur(6px)',
              color: INK,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Chat / plus bubble — "add your voice". */}
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7A8.5 8.5 0 1 1 21 11.5z" />
              <line x1="12" y1="8.5" x2="12" y2="13.5" />
              <line x1="9.5" y1="11" x2="14.5" y2="11" />
            </svg>
          </button>
        )}
      </div>

      {/* Cluster reference key with collapse toggle. MIN width is
          pinned to the toolbar's measured width so the KEY box is at
          least as wide as the search + settings + contribute cluster
          above it — but it's free to grow WIDER for long cluster
          names / the HIDE-ALL row. Falls back to auto until the first
          measure. */}
      {clusterEntries.length > 0 && (
        <div
          data-tour="key"
          style={{
            minWidth: toolbarWidth ? `${toolbarWidth}px` : undefined,
            maxWidth: 'min(320px, 90vw)',
            background: GLASS.panel,
            border: `1px solid ${LINE}`,
            borderRadius: RADIUS.md,
            backdropFilter: 'blur(6px)',
            display: 'flex',
            flexDirection: 'column',
            // Bound the whole panel; the inner list scrolls.
            minHeight: 0,
          }}
        >
          <button
            onClick={() => setKeyOpen(!effectiveKeyOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: INK2,
              fontFamily: FONT.mono,
              fontSize: 10,
              letterSpacing: 0.7,
              textTransform: 'uppercase',
              opacity: 0.75,
              textAlign: 'left',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 8,
                transition: 'transform 140ms ease',
                transform: effectiveKeyOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              ▸
            </span>
            <span style={{ flex: 1 }}>
              key ({clusterEntries.length})
            </span>
            {/* hide/show-all toggle — only when the key is expanded.
                Lives in the header bar (right-justified next to the
                count) so it doesn't take a whole strip of vertical
                space underneath. e.stopPropagation on the click so
                the surrounding button's expand/collapse doesn't
                trigger. */}
            {effectiveKeyOpen && clusterEntries.length > 0 && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  if (hiddenClusterIds.size === 0) {
                    setHiddenClusterIds(
                      new Set(clusterEntries.map((c) => c.id)),
                    );
                  } else {
                    clearHiddenClusterIds();
                  }
                }}
                style={{
                  display: 'inline-block',
                  border: `1px solid ${LINE}`,
                  borderRadius: 3,
                  color: 'rgba(230,232,239,0.6)',
                  fontFamily: FONT.mono,
                  padding: '1px 7px',
                  fontSize: 9,
                  letterSpacing: 0.4,
                  cursor: 'pointer',
                  transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLSpanElement).style.background = 'rgba(255,255,255,0.06)';
                  (e.currentTarget as HTMLSpanElement).style.color = '#ffffff';
                  (e.currentTarget as HTMLSpanElement).style.borderColor = 'rgba(255,255,255,0.20)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLSpanElement).style.background = 'transparent';
                  (e.currentTarget as HTMLSpanElement).style.color = 'rgba(230,232,239,0.6)';
                  (e.currentTarget as HTMLSpanElement).style.borderColor = 'rgba(255,255,255,0.10)';
                }}
              >
                {hiddenClusterIds.size === 0 ? 'hide all' : 'show all'}
              </span>
            )}
            {focusedClusterId && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setFocusedClusterId(null);
                }}
                style={{
                  fontSize: 10,
                  opacity: 0.55,
                  color: BLUE,
                  cursor: 'pointer',
                }}
                title="clear cluster focus"
              >
                clear
              </span>
            )}
          </button>
          {effectiveKeyOpen && (
            <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  maxHeight: 320,
                  overflowY: 'auto',
                  padding: '0 2px 4px',
                }}
              >
                {clusterEntries.map((c) => {
                  const active = focusedClusterId === c.id;
                  const hidden = hiddenClusterIds.has(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => setFocusedClusterId(active ? null : c.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        ...(active
                          ? activePill(BLUE)
                          : { background: 'transparent', border: 'none', color: INK }),
                        borderRadius: 3,
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: 12,
                        textAlign: 'left',
                        opacity: hidden ? 0.42 : 1,
                        transition: 'opacity 120ms ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!active)
                          (e.currentTarget as HTMLButtonElement).style.background =
                            'rgba(255,255,255,0.06)';
                      }}
                      onMouseLeave={(e) => {
                        if (!active)
                          (e.currentTarget as HTMLButtonElement).style.background =
                            'transparent';
                      }}
                    >
                      {/* The colour swatch is itself a clickable
                          toggle for the cluster's visibility. e.stopPropagation
                          so it doesn't ALSO trip the focus action on
                          the row. */}
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleHiddenClusterId(c.id);
                        }}
                        title={hidden ? 'Show this cluster' : 'Hide this cluster'}
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: 2,
                          background: hidden ? 'transparent' : c.color,
                          border: hidden ? `1px solid ${c.color}` : 'none',
                          flexShrink: 0,
                          cursor: 'pointer',
                          transition: 'background 120ms ease, border-color 120ms ease',
                        }}
                      />
                      <span
                        style={{
                          flex: 1,
                          textDecoration: hidden ? 'line-through' : 'none',
                          textDecorationColor: 'rgba(230,232,239,0.4)',
                        }}
                      >
                        {c.label}
                      </span>
                      <span style={{ opacity: 0.5, fontSize: 11 }}>{c.count}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
